// MaturaLinearKIntervalClosedTask.js
// Zadanie maturalne zamknięte – funkcja liniowa
// f(x) = ax - k, warunek na położenie miejsca zerowego,
// wybór przedziału, do którego należy k.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

// ================== HELPERS ==================

const LETTERS = ["A", "B", "C", "D"];

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const pick = (arr) => arr[randInt(0, arr.length - 1)];

// NWD – potrzebne do ładnego formatu ułamka
const gcd = (a, b) => {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) {
    const t = a % b;
    a = b;
    b = t;
  }
  return a || 1;
};

// formatowanie liczby / ułamka jak w innych zadaniach
function formatFraction(num, den) {
  if (den < 0) {
    num = -num;
    den = -den;
  }

  let g = gcd(num, den);
  let N = num / g;
  let D = den / g;

  const sign = N < 0 ? "-" : "";
  N = Math.abs(N);

  if (N % D === 0) {
    return `${sign}${N / D}`;
  }

  const whole = Math.floor(N / D);
  const rest = N % D;

  if (whole > 0) {
    return `${sign}${whole}\\dfrac{${rest}}{${D}}`;
  }
  return `${sign}\\dfrac{${rest}}{${D}}`;
}

// latex dla współczynnika przy x w f(x) = ax - k
function buildFunctionLatex(aNum, aDen) {
  if (aDen < 0) {
    aDen = -aDen;
    aNum = -aNum;
  }
  const aVal = aNum / aDen;
  let aTerm;

  if (aVal === 1) aTerm = "x";
  else if (aVal === -1) aTerm = "-x";
  else {
    const aFrac = formatFraction(aNum, aDen);
    aTerm = `${aFrac}x`;
  }

  return `f(x) = ${aTerm} - k`;
}

// latex dla równania 0 = ax_0 - k
function buildZeroEqLatex(aNum, aDen) {
  if (aDen < 0) {
    aDen = -aDen;
    aNum = -aNum;
  }
  const aVal = aNum / aDen;
  let aTerm;

  if (aVal === 1) aTerm = "x_0";
  else if (aVal === -1) aTerm = "-x_0";
  else {
    const aFrac = formatFraction(aNum, aDen);
    aTerm = `${aFrac}x_0`;
  }

  return `0 = ${aTerm} - k`;
}

// int -> latex bez dodatkowych nawiasów przy liczbie ujemnej
function numberLatex(n) {
  return `${n}`;
}

// latex przedziału (left; right)
function intervalLatex(left, right) {
  return `\\left(${left}; ${right}\\right)`;
}

// ================== GENERATOR ==================

function createJune2025OriginalTask() {
  const options = [
    { label: "A", latex: "\\left(-\\infty; -1\\right)", isCorrect: false },
    { label: "B", latex: "\\left(-1; 0\\right)", isCorrect: false },
    { label: "C", latex: "\\left(0; 1\\right)", isCorrect: false },
    { label: "D", latex: "\\left(1; +\\infty\\right)", isCorrect: true },
  ];

  return {
    type: "MaturaLinearKIntervalClosedTask",
    data: {
      aNum: 1,
      aDen: 2,
      t: 2,
      boundaryK: 1,
      relation: ">",
      options,
      correctIndex: 3,
      isPositiveA: true,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_CZE",
      taskNumber: 11,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_CZE"
  ) {
    return createJune2025OriginalTask();
  }

  // możliwe dodatnie nachylenia a
  const baseSlopes = [
    { aNum: 1, aDen: 2 }, // 1/2
    { aNum: 1, aDen: 1 }, // 1
    { aNum: 2, aDen: 1 }, // 2
    { aNum: 3, aDen: 1 }, // 3
  ];

  // dodatnie / ujemne wersje
  const positiveSlopes = baseSlopes.map(({ aNum, aDen }) => ({
    aNum,
    aDen,
  }));
  const negativeSlopes = baseSlopes.map(({ aNum, aDen }) => ({
    aNum: -aNum,
    aDen,
  }));

  let aNum;
  let aDen;
  let boundaryK; // dodatnia granica dla k: a * t
  let t; // liczba z treści – z którą porównujemy miejsce zerowe

  // relacja dla x0 w treści
  const relation = Math.random() < 0.5 ? ">" : "<"; // dla x0

  while (true) {
    // dobieramy znak a tak, żeby w końcu wyszło k > boundaryK (jak w obecnych odpowiedziach)
    const slopesPool = relation === ">" ? positiveSlopes : negativeSlopes;
    const { aNum: n, aDen: d } = pick(slopesPool);
    aNum = n;
    aDen = d;

    // losujemy dodatnią granicę k (1..4 – małe, ładne liczby)
    const candidateB = randInt(1, 4);

    // t ma być całkowite: t = B * aDen / aNum
    const tNum = candidateB * aDen;
    if (tNum % aNum !== 0) continue;

    const candidateT = tNum / aNum; // może być ujemne
    if (Math.abs(candidateT) > 5) continue;

    boundaryK = candidateB; // > 0
    t = candidateT;
    break;
  }

  const aVal = aNum / aDen;
  const isPositiveA = aVal > 0;

  // przy tak dobranym znaku a i relacji dla x0
  // zawsze ma wyjść nierówność k > boundaryK
  const B = boundaryK;

  // przedziały jak na maturze: (-∞,-B), (-B,0), (0,B), (B,+∞)
  const intervals = [
    { left: "-\\infty", right: numberLatex(-B) },
    { left: numberLatex(-B), right: "0" },
    { left: "0", right: numberLatex(B) },
    { left: numberLatex(B), right: "+\\infty" },
  ];

  const options = intervals.map((intv, idx) => ({
    label: LETTERS[idx],
    latex: intervalLatex(intv.left, intv.right),
    isCorrect: idx === 3, // zawsze D
  }));

  return {
    type: "MaturaLinearKIntervalClosedTask",
    data: {
      aNum,
      aDen,
      t,
      boundaryK: B,
      relation, // ">" albo "<" dla x0
      options,
      correctIndex: 3,
      isPositiveA,
    },
  };
}

// ================== RENDER TREŚCI ==================

export function renderTask({ data }) {
  const { aNum, aDen, t, relation, options } = data;

  const funcLatex = buildFunctionLatex(aNum, aDen);
  const tLatex = numberLatex(t);
  const relText = relation === ">" ? "większą od" : "mniejszą od";

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>
      <p>
        Funkcja liniowa <InlineMath math={"f"} /> jest określona wzorem{" "}
        <InlineMath math={funcLatex} />, gdzie <InlineMath math={"k"} /> jest
        liczbą rzeczywistą.
      </p>
      <p>
        Miejsce zerowe funkcji <InlineMath math={"f"} /> jest liczbą {relText}{" "}
        <InlineMath math={tLatex} />.
      </p>
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>
        Liczba <InlineMath math={"k"} /> należy do przedziału:
      </p>

      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ================== INPUT (ABCD) ==================

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;

  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

// ================== SPRAWDZANIE ==================

export function checkAnswer({ data }, userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !Number.isInteger(userAnswer.choiceIndex)
  ) {
    return false;
  }
  return userAnswer.choiceIndex === data.correctIndex;
}

// ================== ODPOWIEDŹ ==================

export function renderAnswer({ data }) {
  const { options, correctIndex } = data;
  const correct = options[correctIndex];

  // Bez "Poprawna odpowiedź:" – runner i tak ma swój nagłówek
  return <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />;
}

// ================== WYJAŚNIENIE ==================

export function renderExplanation({ data }) {
  const { aNum, aDen, t, boundaryK, relation, isPositiveA } = data;

  const aLatex = formatFraction(aNum, aDen); // "a" jako ułamek/liczba
  const tLatex = numberLatex(t);
  const kBoundaryLatex = numberLatex(boundaryK);

  const zeroEq = buildZeroEqLatex(aNum, aDen);

  // a * x0 = k  ->  x0 = k / a
  const x0Latex = `x_0 = \\dfrac{k}{${aLatex}}`;

  // Warunek z treści: x0 > t lub x0 < t
  const relText = relation === ">" ? "większe niż" : "mniejsze niż";

  // Przejście do nierówności na k:
  // Dla a > 0: mnożymy nierówność przez a – znak zostaje
  // Dla a < 0: mnożymy przez a – znak się odwraca
  // Generator jest tak ustawiony, że finalnie zawsze wyjdzie k > boundaryK.
  const aSignText = isPositiveA ? "dodatni" : "ujemny";
  const afterMultiplyRelation = isPositiveA ? relation : relation === ">" ? "<" : ">";

  const stepIneq1 = `\\dfrac{k}{${aLatex}} ${relation} ${tLatex}`;
  const stepIneq2 = `k ${afterMultiplyRelation} \\left(${aLatex}\\right)\\cdot \\left(${tLatex}\\right)`;
  const stepIneq3 = `k > ${kBoundaryLatex}`;

  const solutionIntervalLatex = intervalLatex(kBoundaryLatex, "+\\infty");

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Wyznaczamy miejsce zerowe{" "}
        <InlineMath math={"x_0"} /> z równania <InlineMath math={"f(x)=0"} />:
      </p>
      <BlockMath math={"f(x_0)=0"} />
      <BlockMath math={zeroEq} />
      <BlockMath math={`\\Rightarrow\\; ${aLatex}x_0 = k`} />
      <BlockMath math={`\\Rightarrow\\; ${x0Latex}`} />

      <p>
        <strong>Krok 2.</strong> Z treści zadania wiemy, że miejsce zerowe jest{" "}
        {relText} <InlineMath math={tLatex} />, czyli:
      </p>
      <BlockMath math={`x_0 ${relation} ${tLatex}`} />

      <p>
        <strong>Krok 3.</strong> Podstawiamy{" "}
        <InlineMath math={"x_0=\\dfrac{k}{a}"} /> i rozwiązujemy nierówność.{" "}
        Ponieważ <InlineMath math={"a"} /> jest {aSignText}{" "}
        (<InlineMath math={`a=${aLatex}`} />), po pomnożeniu przez{" "}
        <InlineMath math={"a"} /> znak nierówności{" "}
        {isPositiveA ? "zostaje bez zmian" : "odwraca się"}:
      </p>

      <BlockMath math={stepIneq1} />
      <BlockMath math={`\\Rightarrow\\; ${stepIneq2}`} />
      <BlockMath math={`\\Rightarrow\\; ${stepIneq3}`} />

      <p>
        Zatem <InlineMath math={"k"} /> należy do przedziału{" "}
        <InlineMath math={solutionIntervalLatex} />.
      </p>
    </div>
  );
}

// ================== ODPOWIEDŹ UCZNIA ==================

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    !userAnswer.latex
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;

  return (
    <div style={{ marginTop: "1rem", textAlign: "left" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}
